import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericCard from "@ui-components/Card/GenericCard";
import ListPageLayout from "@ui-components/ListPage/ListPageLayout";
import ListPageLoading from "@ui-components/ListPage/ListPageLoading";
import ListPageToolbar from "@ui-components/ListPage/ListPageToolbar";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import {
  buildPredicate,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { ReactComponent as IconDownload } from "../../../../shared/assets/Icons/download-solid.svg";
import { useAppSelector } from "@redux/hooks";
import { RequireTenantType } from "@shared/components/RequireTenantType";
import { useRequireTenantType } from "@shared/hooks/useRequireTenantType";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { RefreshingIndicator } from "@shared/components/ListLoading";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useDetailTabParam } from "@shared/navigation/tabs/useDetailTabParam";
import {
  REPORT_PHONE_ORDERS,
  REPORT_TABS,
  Report,
  ReportPhoneOrder,
  getReportTab,
  getUndownloadableReason,
  isReportDownloadable,
  reportsBaseColumns,
  sortReportsForPhone,
} from "./ReportsData";
import { makeReportFields } from "./reportsFieldSpec";
import {
  fetchReportDownload,
  fetchReportsList,
  readApiErrorMessage,
} from "./request/request";
import { useReportsAutoRefresh } from "./useReportsAutoRefresh";
import { useWindowSize } from "@shared/hooks/useWindowSize";

/** The page is a partner/admin surface; customers never reach it. */
const ALLOWED_TENANT_TYPES = ["PARTNER", "GLOBAL_ADMIN"] as const;

export default function ReportsPage() {
  // ========== Access Control ==========
  // Check access status to prevent unnecessary API calls
  const { isAllowed } = useRequireTenantType([...ALLOWED_TENANT_TYPES]);

  const api = useAxiosAuth0();
  const { isAuthenticated, isLoading } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);

  // ========= Table data =========
  const [reports, setReports] = useState<Report[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [listFetchError, setListFetchError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  // A tab per report type (CTX-1192), kept in `?tab=` like the detail pages'
  // sub-tabs so a reload or a saved view lands on the same one.
  const [activeTab, setActiveTab] = useDetailTabParam(
    REPORT_TABS,
    REPORT_TABS[0]
  );

  // ========== Download state ==========
  const [downloadingReportId, setDownloadingReportId] = useState<number | null>(
    null
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const canFetch =
    isAllowed && !isLoading && isAuthenticated && Boolean(tenantId);

  // ------------------------------------------------------------------------------------
  // Loading the list
  //
  // `showLoading` separates the two reasons this runs. A foreground load (mount,
  // tenant switch) owns the page's loading and error surfaces. A background
  // refresh — the in-progress poll — must never blank the table or replace it
  // with an error card over a single failed tick: the rows on screen are still
  // the best answer we have.
  // ------------------------------------------------------------------------------------
  const loadReports = useCallback(
    async (showLoading: boolean) => {
      if (!canFetch) return;
      try {
        if (showLoading) {
          setIsFetching(true);
          setListFetchError(null);
        }
        const list = await fetchReportsList(api, tenantId);
        setReports(list);
      } catch (err) {
        console.error("Error fetching reports:", err);
        if (showLoading) {
          setReports([]);
          setListFetchError(
            readApiErrorMessage(err, "Failed to load reports")
          );
        }
      } finally {
        if (showLoading) {
          setIsFetching(false);
          setHasFetched(true);
        }
      }
    },
    [api, canFetch, tenantId]
  );

  useEffect(() => {
    if (!canFetch) return;
    setHasFetched(false);
    void loadReports(true);
  }, [canFetch, loadReports]);

  const refreshReports = useCallback(() => {
    void loadReports(false);
  }, [loadReports]);

  // Keeps `pending` / `generating` rows moving without a manual reload: the
  // monthly job leaves them in flight while it runs, early on the 1st.
  const isPolling = useReportsAutoRefresh({
    enabled: canFetch,
    reports,
    onRefresh: refreshReports,
  });

  // ------------------------------------------------------------------------------------
  // Filters
  // ------------------------------------------------------------------------------------
  const isPhone = useWindowSize().width < 640;
  const reportFields = useMemo(() => makeReportFields(), []);

  const normalizedFilters = useMemo(
    () =>
      normalizeFilters<Report>(activeFilters, reportFields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, reportFields]
  );

  const predicate = useMemo(
    () => buildPredicate<Report>(normalizedFilters, reportFields),
    [normalizedFilters, reportFields]
  );

  // The search narrows the active tab only. The filters survive a tab switch,
  // so a customer searched in one tab can be found in the other.
  const tabReports = useMemo(
    () => reports.filter((report) => getReportTab(report) === activeTab),
    [reports, activeTab]
  );

  // On a phone the list reads newest first or grouped by customer; the table
  // keeps its own column sort on wider screens.
  const [phoneOrder, setPhoneOrder] = useState<ReportPhoneOrder>("Date");

  const filteredData = useMemo(() => {
    const matching = tabReports.filter(predicate);
    return isPhone ? sortReportsForPhone(matching, phoneOrder) : matching;
  }, [tabReports, predicate, isPhone, phoneOrder]);

  const suggestedKeys = useMemo(
    () => reportFields.map((field) => field.label),
    [reportFields]
  );

  // ------------------------------------------------------------------------------------
  // Download
  // ------------------------------------------------------------------------------------
  const handleDownload = useCallback(
    async (report: Report) => {
      if (downloadingReportId === report.id) return; // Prevent duplicate downloads

      try {
        setDownloadingReportId(report.id);

        const { downloadUrl, filename } = await fetchReportDownload(
          api,
          report.id,
          tenantId
        );

        // Signed URL: hand it to the browser rather than buffering the PDF here.
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download =
          filename || `Report_${report.reportMonth}_${report.reportYear}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading report:", error);
        setDownloadError(
          readApiErrorMessage(
            error,
            "Failed to download report. Please try again."
          )
        );
      } finally {
        setDownloadingReportId(null);
      }
    },
    [api, downloadingReportId, tenantId]
  );

  // ------------------------------------------------------------------------------------
  // Columns — base plus the per-row action, which closes over the download state
  // ------------------------------------------------------------------------------------
  const columns = useMemo<GenericTableColumn<Report>[]>(
    () => [
      ...reportsBaseColumns,
      {
        header: "Actions",
        accessor: "id",
        renderCell: (row: Report) => {
          // A row with no file behind it says why, instead of offering a
          // button that cannot work.
          const undownloadable = getUndownloadableReason(row);
          if (undownloadable) {
            return (
              <span
                className={
                  undownloadable.tone === "danger"
                    ? "text-xs text-semantic-text-danger"
                    : "text-xs text-semantic-text-secondary"
                }
                title={undownloadable.detail ?? undownloadable.message}
              >
                {undownloadable.message}
              </span>
            );
          }
          const isDownloading = downloadingReportId === row.id;
          const download = (e: React.MouseEvent) => {
            e.stopPropagation();
            void handleDownload(row);
          };
          const isDisabled = isDownloading || !isReportDownloadable(row);
          return (
            <>
              {/* Phone: the card's closing action, named — the table's 32px icon
                  was easy to miss. Secondary, so 10 of them do not shout (CTX-1383). */}
              <div className="sm:hidden">
                <GenericButton
                  variant="secondary"
                  label="Download"
                  size="sm"
                  onClick={download}
                  icon={<IconDownload className="w-4 fill-current" />}
                  loading={isDownloading}
                  disabled={isDisabled}
                />
              </div>
              <div className="hidden items-center gap-2 sm:flex">
                <GenericButton
                  variant="brand"
                  label=""
                  size="sm"
                  isIconOnly={true}
                  onClick={download}
                  icon={<IconDownload className="w-4 text-white fill-white" />}
                  loading={isDownloading}
                  disabled={isDisabled}
                />
              </div>
            </>
          );
        },
      },
    ],
    [downloadingReportId, handleDownload]
  );

  // ------------------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------------------
  const showInitialSkeleton =
    canFetch && (!hasFetched || (isFetching && reports.length === 0));
  // Rows are already on screen and a newer answer is on its way: the thin bar,
  // not a spinner — what is rendered stays usable while it lands.
  const isRefreshingList = isFetching && reports.length > 0;

  // An empty tab keeps the tabs and the searchbar around it, so the other tab
  // stays one click away. Nothing here can make a report — the monthly job does,
  // and the empty state says when (its copy lives in EmptyItems' route config).
  const renderTabBody = () => {
    if (tabReports.length === 0) return <EmptyItems inline />;
    return (
      <GenericTable
        mobileCards={{ titleColumn: "Customer Name", badgeColumn: "Status", headlineColumn: "Date", footerColumns: ["Actions"], prominent: true }}
        columns={columns}
        data={filteredData}
        // On a phone every report as a card ran to 101 cards on one page; the
        // pager takes 10 at a time there (CTX-1383).
        initialRowsPerPage={
          isPhone ? 10 : filteredData.length > 0 ? Math.max(10, filteredData.length) : 10
        }
        rowsPerPageOptions={
          [
            10,
            25,
            50,
            100,
            filteredData.length > 100 ? filteredData.length : undefined,
          ].filter(Boolean) as number[]
        }
        tableClassName="bg-[var(--semantic-bg-primary)]"
        width="100%"
        maxTableBodyHeight="100%"
      />
    );
  };

  const renderContent = () => {
    // The page's own shape in skeleton, inside the content area. The overlay this
    // replaces covered the rail and the views bar too (CTX-1122).
    if (showInitialSkeleton)
      return <ListPageLoading columns={columns.length} />;
    if (canFetch && listFetchError) {
      return <ErrorFetchingItems text={listFetchError} />;
    }

    return (
      <ListPageLayout>
        <RefreshingIndicator active={isRefreshingList} />
        <div className="relative flex-1 min-h-0">
          <GenericCard
            isCollapsible={false}
            className="h-full flex flex-col"
            bodyContent={
              <div className="flex flex-col h-full min-h-0">
                <ListPageToolbar className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  {/* Compact and content-sized, like the other list-level tab
                      bars: it shares the searchbar's row instead of
                      spending a row of its own. */}
                  <div className="flex shrink-0 items-center justify-between gap-2">
                    <GenericTabs
                      tabs={REPORT_TABS.map((tab) => ({
                        label: tab,
                        value: tab,
                      }))}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      size="xs"
                      fullWidth={false}
                      fitContent
                      aria-label="Report type"
                    />
                    {isPhone && (
                      <GenericTabs
                        tabs={REPORT_PHONE_ORDERS.map((order) => ({
                          label: order,
                          value: order,
                        }))}
                        activeTab={phoneOrder}
                        onTabChange={(order) =>
                          setPhoneOrder(order as ReportPhoneOrder)
                        }
                        size="xs"
                        fullWidth={false}
                        fitContent
                        aria-label="Order"
                      />
                    )}
                  </div>
                  <div className="w-full sm:w-[520px] sm:mr-auto">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                  {isPolling && (
                    <span className="text-xs text-semantic-text-secondary">
                      Updating…
                    </span>
                  )}
                </ListPageToolbar>

                <div className="flex-1 min-h-0 flex flex-col">
                  {renderTabBody()}
                </div>
              </div>
            }
          />
        </div>
      </ListPageLayout>
    );
  };

  return (
    <RequireTenantType allowedTypes={[...ALLOWED_TENANT_TYPES]}>
      {renderContent()}

      <GenericAlert
        isOpen={Boolean(downloadError)}
        onClose={() => setDownloadError(null)}
        onConfirm={() => setDownloadError(null)}
        variant="error"
        title="Download Error"
        description={downloadError ?? ""}
        confirmLabel="OK"
        hideCancel={true}
      />
    </RequireTenantType>
  );
}
